import { env } from "../config/env";
import { parseMediaNodeId } from "../lib/media/parseMediaNodeId";
import { ApiError, getRequestAuthHeaders } from "./client";

const MAX_FORM_MEDIA_BYTES = 15 * 1024 * 1024;

export type FormMediaUploadResult = {
  url: string;
  previewUrl: string | null;
  nodeId: number | null;
};

type FormMediaUploadResponse = {
  message?: string;
  url?: string;
  contentUrl?: string;
  previewUrl?: string | null;
  nodeId?: number | string | null;
};

function readErrorMessage(status: number, body: unknown): string {
  if (status === 401) {
    return "Не удалось загрузить картинку: нет доступа к хранилищу. Форма не сброшена — сохраните её и попробуйте снова.";
  }
  if (
    body &&
    typeof body === "object" &&
    "message" in body &&
    typeof body.message === "string" &&
    body.message.trim()
  ) {
    return body.message.trim();
  }
  return `Не удалось загрузить картинку (${status})`;
}

/** POST /api/v1/forms/media/upload — публичный URL диска для фона и display. */
export async function uploadFormMedia(file: File): Promise<FormMediaUploadResult> {
  if (!file.type.startsWith("image/")) {
    throw new ApiError("Можно загрузить только изображение", 400);
  }
  if (file.size > MAX_FORM_MEDIA_BYTES) {
    throw new ApiError("Файл больше 15 МБ", 400);
  }

  const body = new FormData();
  body.append("file", file);

  const response = await fetch(`${env.apiBaseUrl}/api/v1/forms/media/upload`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...getRequestAuthHeaders(),
    },
    body,
  });

  const json = (await response.json().catch(() => null)) as
    | FormMediaUploadResponse
    | null;

  if (!response.ok) {
    throw new ApiError(readErrorMessage(response.status, json), response.status);
  }

  const url = String(json?.url ?? json?.contentUrl ?? "").trim();
  if (!url || !/^https?:\/\//i.test(url)) {
    throw new ApiError("В ответе загрузки нет публичного URL", 500);
  }

  const preview = String(json?.previewUrl ?? "").trim();
  return {
    url,
    previewUrl: preview && /^https?:\/\//i.test(preview) ? preview : null,
    nodeId: parseMediaNodeId(json?.nodeId),
  };
}

export async function uploadDisplayImage(file: File): Promise<string> {
  const uploaded = await uploadFormMedia(file);
  return uploaded.url;
}
